import { X } from 'lucide-react';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';

export type PhotoPaneItem = { id: string; thumbnailUrl: string; caption?: string; lineLabel: string };

export type OrderServicePhotoPaneProps = {
  serviceName: string;
  unitPriceText?: string;
  addressLine?: string;
  photos: PhotoPaneItem[];
  note: string;
  onNoteChange: (v: string) => void;
  onRemovePhoto: (id: string) => void;
  noteRequired: boolean;
  /**
   * Desktop dialog: true (default) — the pane is a bounded flex column and its
   * photo list scrolls internally against the dialog's max height.
   * Sub-lg sheet: false — the pane flows and the SHEET scrolls as one column.
   * Same host-decides pattern as AssetCardList's constrainHeight, and for the
   * same reason found on-device 2026-08-21: with two independently-scrolling
   * panes in one capped sheet, a tall summary squeezed this pane's list toward
   * zero height and the photos vanished.
   */
  constrainHeight?: boolean;
};

export default function OrderServicePhotoPane({
  serviceName, unitPriceText, addressLine, photos, note, onNoteChange, onRemovePhoto, noteRequired,
  constrainHeight = true,
}: OrderServicePhotoPaneProps) {
  return (
    // Bounded flex column when constrained: the heading, count and note stay
    // put while the photo list (flex-1, min-h-0) scrolls against the dialog's
    // max height. Unconstrained, everything flows and the host scrolls.
    <div className={cn('flex flex-col', constrainHeight ? 'min-h-0 p-6' : 'p-4')}>
      <Typography variant="heading-md" as="h2" className="shrink-0">
        {serviceName}{' '}
        {unitPriceText && (
          <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-sm text-primary font-normal">{unitPriceText}</span>
        )}
      </Typography>
      {addressLine && <Typography as="p" variant="body" className="shrink-0 text-muted-foreground">{addressLine}</Typography>}

      <Typography as="p" variant="body-semibold" className="mt-4 shrink-0 pb-2">
        {photos.length} {photos.length === 1 ? 'photo' : 'photos'} in this order
      </Typography>
      <ul
        className={cn(
          'mt-2 flex flex-col gap-2 pr-1',
          constrainHeight && 'min-h-0 flex-1 overflow-y-auto'
        )}
      >
        {photos.map((photo) => (
          <li key={photo.id} className="flex shrink-0 items-center gap-3 rounded-md border border-border p-2">
            <img src={photo.thumbnailUrl} alt="" className="h-12 w-16 rounded object-cover" />
            <div className="flex-1">
              <Typography as="p" variant="body-medium">{photo.caption}</Typography>
              <Typography as="p" variant="body" className="text-muted-foreground">{photo.lineLabel}</Typography>
            </div>
            <button type="button" aria-label={`Remove ${photo.caption}`} onClick={() => onRemovePhoto(photo.id)}><X className="h-4 w-4 text-muted-foreground" /></button>
          </li>
        ))}
      </ul>

      <label className="mt-4 block shrink-0">
        <Typography variant="body-semibold">Request Details {noteRequired && <span className="text-destructive">*</span>}</Typography>
        <Textarea
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          placeholder="Enter request details here"
          rows={3}
          className="mt-1"
        />
      </label>
    </div>
  );
}
